import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Form, FormGroup, FormLabel, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from "framer-motion";
import MainLogo from "../../../../images/main-logo.svg";
import LoginImage from "../../../../images/otp-image.png";
import AuthLeftSection from "../AuthLeftSection/AuthLeftSection.tsx";
import AuthBackground from "../AuthBackground/AuthBackground.tsx";
import apiConfig from "../../config/apiConfig.js";
import { useFlowData } from '../hooks/useFlowData.js';
import useCountdown from '../hooks/useCountDown.js';
import { API_ENDPOINTS } from '../utils/constants.js';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./Otp.scss";

function Otp() {
  const navigate = useNavigate();
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
   const [isLoadingResend, setIsLoadingResend] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  
  // Fix: Properly type the refs array
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const { email: userEmail, isFromForgotPassword, hasValidFlow } = useFlowData();
  const { time, canResend, start, formatTime } = useCountdown(60);

  // Focus on first input when component mounts - MOVED BEFORE CONDITIONAL LOGIC
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
    start();
  }, [start]);


  // Handle redirects for invalid flow data using useEffect
  useEffect(() => {
    if (!hasValidFlow || !userEmail) {
      toast.error('Please try again.');
      navigate(isFromForgotPassword ? '/forgot' : '/signup');
    }
  }, [hasValidFlow, userEmail, isFromForgotPassword, navigate]);

  const handleInputChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    //Add null check before calling focus
    if (value && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ' ') {
      e.preventDefault();
      return;
    }

    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0 && inputRefs.current[index - 1]) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
      }
    }

    if (e.key === 'ArrowLeft' && index > 0 && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    if (pastedData.length > 0) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedData[i] || '';
      }
      setOtp(newOtp);
      setError('');
      
      // Add null check before calling focus
      const focusIndex = Math.min(pastedData.length, 5);
      if (inputRefs.current[focusIndex]) {
        inputRefs.current[focusIndex]?.focus();
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join('');

    if (otpString.length !== 6) {
      setError('Please enter a complete 6-digit OTP');
      return;
    }

    if (!/^\d{6}$/.test(otpString)) {
      setError('OTP must contain only numbers');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(
    `${apiConfig.apiBaseURL}${API_ENDPOINTS.VERIFY_OTP}`,
    {
      email: userEmail,
      otp: otpString,
    },
    { headers: { 'Content-Type': 'application/json' } }
  );


    if (response.data.status === 'Success') {
      if (isFromForgotPassword) {
        // Forgot password flow
        localStorage.removeItem('forgotPasswordEmail');
        
        // Check for different possible token field names
        const token = response.data.authToken || response.data.resetToken;
        
        if (token) {
          localStorage.setItem('authToken', token);
          
        }
        
        localStorage.setItem('verifiedEmail', userEmail);
        toast.success(response.data.message || 'OTP verified successfully!');
        
        navigate('/create-password', { 
          state: { 
            flow: 'forgot', 
            email: userEmail 
          } 
        });
      } else {
        // Signup flow
        localStorage.removeItem('pendingUser');
        
        // Check for different possible token field names
        const token = response.data.authToken || response.data.resetToken;
        
        if (token) {
          localStorage.setItem('authToken', token);
        }
        
        localStorage.setItem('verifiedEmail', userEmail);
        
        toast.success(response.data.message || 'Email verified successfully!');
        
        navigate('/create-password', { 
          state: { 
            flow: 'signup', 
            email: userEmail 
          } 
        });
      }
    } else {
      setError(response.data.message || 'Invalid OTP. Please try again.');
      toast.error(response.data.message || 'Invalid OTP. Please try again.');
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Verification failed. Please try again.';
    setError(errorMessage);
    toast.error(errorMessage);
  } finally {
    setIsLoading(false);
  }
};

  const handleResendOTP = async () => {
    if (!canResend || isLoadingResend) return;
    
    setIsLoadingResend(true);

    try {
     const response = await axios.post(
       `${apiConfig.apiBaseURL}${API_ENDPOINTS.RESEND_OTP}`,
       {
         email: userEmail,
       }
     );

      if (response.data.status === "Success") {
        toast.success("OTP resent successfully! Please check your email.");
        setOtp(["", "", "", "", "", ""]);
        setError("");
        
        //Add null check before calling focus
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }

        // Start countdown again after successful resend
        start();
      } else {
        toast.error(response.data.message || "Failed to resend OTP.");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to resend OTP.");
    } finally {
      setIsLoadingResend(false);
    }
  };


  // Return null if invalid flow, but after all hooks have been called
  if (!hasValidFlow || !userEmail) {
    return null;
  }

  // Animation variants for smooth, calm transitions
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        staggerChildren: 0.2
      }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -60, scale: 0.95 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 1,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 60, scale: 0.95 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 1,
        ease: [0.25, 0.46, 0.45, 0.94],
        delay: 0.3
      }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const floatingAnimation = {
    animate: {
      y: [0, -8, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const buttonHover = {
    scale: 1.02,
    transition: { duration: 0.2, ease: "easeInOut" }
  };

  const otpInputAnimation = {
    focus: {
      scale: 1.05,
      boxShadow: "0 0 0 3px rgba(57, 87, 160, 0.1)",
      borderColor: "#3957A0",
      transition: { duration: 0.2 }
    }
  };

  return (
    <>
      {/* Full-screen background animations */}
      <AuthBackground />

      <motion.section
        className="login-screen"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 15px', position: 'relative', zIndex: 1 }}
      >
        <div style={{ maxWidth: '1600px', width: '100%' }}>
        <div className="row" style={{ width: '100%', margin: '0' }}>
        {/* Left Panel - Enhanced Image and Content */}
        <AuthLeftSection
          image={LoginImage}
          imageAlt="OTP"
          title="Giving Presence to Future Moments"
          description="We’re more than a service—it’s a movement to redefine how love endures."
          slideInLeft={slideInLeft}
          fadeInUp={fadeInUp}
          floatingAnimation={floatingAnimation}
        />

        {/* Right Panel - OTP Form */}
        <div className="col-md-6" style={{ display: 'flex' }}>
          {/* OUTER WRAPPER - Has background and padding */}
          <motion.div
            className="login-screen-right-wrapper"
            variants={slideInRight}
            initial="hidden"
            animate="visible"
            style={{
              height: 'calc(100vh - 20px)',
              width: '100%',
              background: '#F2F3F7',
              borderRadius: '24px'
            }}
          >
            {/* INNER SCROLLABLE CONTAINER */}
            <div
              className="login-screen-right"
              style={{
                height: '100%',
                overflowY: 'auto',
                width: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div className="login-screen-right-in" style={{ background: 'transparent' }}>
            <motion.div
              className="middle-block"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                ease: "easeOut",
                delay: 0.6
              }}
            >
              <motion.figure
                className="main-logo"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  duration: 0.8,
                  ease: "easeOut",
                  delay: 0.8
                }}
                whileHover={{ scale: 1.05 }}
              >
                <img src={MainLogo} alt="Main Logo" />
              </motion.figure>

              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  ease: "easeOut",
                  delay: 1
                }}
                style={{ marginBottom: '1rem', color: '#2c3e50' }}
              >
                One Time Passcode
              </motion.h3>

              <motion.p
                className="otp-text"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  ease: "easeOut",
                  delay: 1.1
                }}
                style={{ marginBottom: '2rem', color: '#677289', lineHeight: '1.5' }}
              >
                We have sent the verification code to your email <br />
                address <span style={{ color: '#3957A0', fontWeight: '600' }}>{userEmail}</span>
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.3, duration: 0.6 }}
              >
                <Form onSubmit={handleSubmit}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.4, duration: 0.6 }}
                  >
                    <FormGroup className="mb-4 otp-block">
                      <FormLabel style={{ fontWeight: '600', color: '#2c3e50' }}>One-Time Passcode</FormLabel>
                      <motion.div
                        className="d-flex gap-2"
                        style={{ justifyContent: 'center', marginTop: '1rem' }}
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 1.6, duration: 0.5 }}
                      >
                        {otp.map((digit, index) => (
                          <motion.input
                            key={index}
                            ref={(el) => {
                              inputRefs.current[index] = el;
                            }}
                            type="text"
                            inputMode="numeric"
                            pattern="\d*"
                            maxLength={1}
                            className="form-control text-center otp-input"
                            value={digit}
                            onChange={(e) =>
                              handleInputChange(index, e.target.value)
                            }
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={handlePaste}
                            disabled={isLoading || isLoadingResend}
                            autoComplete="off"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{
                              delay: 1.7 + index * 0.1,
                              duration: 0.3,
                              type: "spring",
                              stiffness: 200
                            }}
                            whileFocus={otpInputAnimation.focus}
                            whileHover={{ scale: 1.02 }}
                            style={{
                              width: '50px',
                              height: '50px',
                              fontSize: '18px',
                              fontWeight: '600',
                              border: '2px solid #e0e6ed',
                              borderRadius: '12px',
                              transition: 'all 0.3s ease'
                            }}
                          />
                        ))}
                      </motion.div>
                      <AnimatePresence>
                        {error && (
                          <motion.small
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            style={{
                              color: "#dc3545",
                              display: "block",
                              marginTop: "12px",
                              fontSize: "0.875rem",
                              textAlign: "center"
                            }}
                          >
                            {error}
                          </motion.small>
                        )}
                      </AnimatePresence>
                    </FormGroup>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2, duration: 0.6 }}
                    whileHover={buttonHover}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      className="main-bttn"
                      type="submit"
                      disabled={isLoading || otp.join("").length !== 6 || isLoadingResend}
                      style={{
                        width: '100%',
                        padding: '14px 20px',
                        background: (isLoading || otp.join("").length !== 6 || isLoadingResend)
                          ? 'linear-gradient(135deg, #bbb, #999)' 
                          : '#D7A360',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '600',
                        boxShadow: '0 8px 20px rgba(215, 163, 96, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <AnimatePresence mode="wait">
                        {isLoading ? (
                          <motion.span
                            key="loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            <motion.span
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              style={{ display: 'inline-block', marginRight: '8px' }}
                            >
                              ⟳
                            </motion.span>
                            Verifying...
                          </motion.span>
                        ) : (
                          <motion.span
                            key="submit"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            Submit
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </Button>
                  </motion.div>

                  <motion.div
                    className="account"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2.2, duration: 0.6 }}
                    style={{ textAlign: 'center', marginTop: '1.5rem' }}
                  >
                    Haven't received One Time Password?{" "}
                    {canResend ? (
                      <motion.button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => {
                          handleResendOTP();
                          start();
                        }}
                        disabled={isLoading || isLoadingResend}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        style={{
                          fontWeight: "600",
                          color: "#3957A0",
                          fontSize: "inherit",
                          textDecoration: "none",
                          cursor: isLoadingResend ? "not-allowed" : "pointer",
                          backgroundColor: "transparent",
                          border: "none",
                        }}
                      >
                        {isLoadingResend ? (
                          <>
                            <motion.span
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              style={{ display: 'inline-block', marginRight: '4px' }}
                            >
                              ⟳
                            </motion.span>
                            Sending...
                          </>
                        ) : (
                          "Resend"
                        )}
                      </motion.button>
                    ) : (
                      <motion.span
                        animate={{ opacity: [0.7, 1, 0.7] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        style={{
                          fontWeight: "600",
                          color: "#6c757d",
                          fontSize: "inherit",
                        }}
                      >
                        Resend in {formatTime(time)}
                      </motion.span>
                    )}
                  </motion.div>
                </Form>
              </motion.div>
            </motion.div>
          </div>
          </div>
          </motion.div>
        </div>
      </div>
      </div>
      </motion.section>
    </>
  );
}

export default Otp;
